import { useCallback, useState } from "react";
import useSWR, { mutate } from "swr";
import {
  type UserSearchHistoryReceptionBookResponseType,
  type UserSearchHistoryReceptionBookListResponseType,
} from "../types";
import { useApiClient } from "@/hooks/useApiClient";

export const useReceptionBookSearchHistory = (
  shouldFetch = false
): {
  searchHistoryData: UserSearchHistoryReceptionBookResponseType[];
  totalCount: number;
  isLoading: boolean;
  isError: boolean;
  refetch: () => Promise<UserSearchHistoryReceptionBookResponseType[]>;
} => {
  const { apiClient } = useApiClient();
  const [isManualLoading, setIsManualLoading] = useState(false);

  const fetcher = useCallback(
    async (
      url: string
    ): Promise<UserSearchHistoryReceptionBookListResponseType> => {
      const response =
        await apiClient.get<UserSearchHistoryReceptionBookListResponseType>(
          url
        );
      return response.data;
    },
    [apiClient]
  );

  const { data, error } =
    useSWR<UserSearchHistoryReceptionBookListResponseType>(
      shouldFetch ? "/user-search-history/reception-book" : null,
      fetcher,
      {
        revalidateOnFocus: false,
        revalidateIfStale: false,
        revalidateOnReconnect: false,
        suspense: false,
        shouldRetryOnError: false,
      }
    );

  // 必要なときに手動でデータを取得するための再取得関数
  const refetch = useCallback(async (): Promise<
    UserSearchHistoryReceptionBookResponseType[]
  > => {
    try {
      setIsManualLoading(true);
      const result = await fetcher("/user-search-history/reception-book");
      // 第3引数をfalseにして再検証を防止する
      void mutate("/user-search-history/reception-book", result, false);
      return result.items;
    } catch (e) {
      console.error("検索履歴の取得に失敗しました:", e);
      void mutate("/user-search-history/reception-book", undefined, false);
      throw e;
    } finally {
      setIsManualLoading(false);
    }
  }, [fetcher]);

  const isLoading = (shouldFetch && !error && !data) || isManualLoading;

  return {
    searchHistoryData: data?.items ?? [],
    totalCount: data?.count ?? 0,
    isLoading,
    isError: !!error,
    refetch,
  };
};
